import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Primitives";

// Mirrors backend/psa.py's CLOSED_STATES. A closed (or resolved) ticket can
// never be a valid survivor -- server.py's merge route 409s on it every
// time -- so it is filtered out of the CANDIDATE list rather than offered
// as a guaranteed-to-fail option. GET /tickets takes one `status` value at
// a time, not "anything but these two", so this runs client-side against
// the (already server-search-scoped, ~25-row) result page, not against a
// locally cached copy of the whole table.
const CLOSED_STATES = ["resolved", "closed"];

/** Merge a duplicate ticket into its survivor.
 *
 *  `ticket` (the prop) is the LOSER -- the one open on the page, and the one
 *  the URL path of `POST /tickets/{ticket_id}/merge` names. The picker below
 *  chooses the SURVIVOR, which the request body calls `into_ticket_id`.
 *  Reversing those two silently merges the wrong ticket and looks fine, so
 *  this component never lets the path and the body disagree about which is
 *  which: `ticket` never moves, only `survivor` (the local selection) does.
 *
 *  Search runs SERVER-SIDE (`GET /tickets?q=...`), debounced, rather than
 *  filtering a client-side cache: a one-shot `limit=200` fetch only ever
 *  reached the newest 200 of 3,542+ tickets on dev (the lowest reachable
 *  number was #14579 -- 94% of the table was invisible to this control no
 *  matter what was typed). The backend's own `q` now matches the ticket
 *  NUMBER as well as the title (server.py's `list_tickets`), so "by number
 *  or title" is true against the whole table, not just a recent slice.
 *
 *  Candidates are fetched WITHOUT scoping to the loser's client -- a
 *  technician can deliberately pick a ticket from another client, and the
 *  server's own refusal ("that ticket belongs to a different client…") is
 *  what should stop them, not a filter that quietly hides the mistake. */
export default function MergeTicketDialog({ ticket, onClose, onMerged }) {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [clientNames, setClientNames] = useState({});
  const [loadingList, setLoadingList] = useState(true);
  const [survivor, setSurvivor] = useState(null); // chosen candidate -> step 2
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClientNames(Object.fromEntries(
      asArray(r.data).map((x) => [x.id, x.name])))).catch(() => setClientNames({}));
  }, []);

  // Debounced server-side search. Runs once immediately for the empty query
  // too (shows the most recent tickets before anyone types anything), then
  // again 300ms after the caller stops typing -- not on every keystroke,
  // which would fire one request per character against the whole table.
  useEffect(() => {
    setLoadingList(true);
    const params = { limit: 25 };
    if (q.trim()) params.q = q.trim();
    const h = setTimeout(() => {
      api.get("/tickets", { params })
        .then((r) => {
          const items = r.data && typeof r.data === "object" && r.data.items
            ? r.data.items : asArray(r.data);
          setCandidates(items.filter((x) => x.id !== ticket.id));
        })
        .catch(() => setCandidates([]))
        .finally(() => setLoadingList(false));
    }, q.trim() ? 300 : 0);
    return () => clearTimeout(h);
  }, [q, ticket.id]);

  const filtered = candidates.filter((c) => !CLOSED_STATES.includes(c.status));

  const confirmMerge = async () => {
    if (!survivor) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/tickets/${ticket.id}/merge`, { into_ticket_id: survivor.id });
      toast.success(`#${ticket.number} merged into #${survivor.number}`);
      onMerged?.(survivor);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose?.(); }}>
      <DialogContent data-testid="merge-ticket-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Merge #{ticket.number}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {survivor
              ? "Confirm which ticket absorbs this one."
              : "Pick the survivor by ticket number or title. This ticket will close and its comments, attachments and time move to whichever one you choose."}
          </DialogDescription>
        </DialogHeader>

        {!survivor && (
          <div className="space-y-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by # or title…" data-testid="merge-search-input"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
            <div className="max-h-72 overflow-y-auto border border-zinc-900 rounded divide-y divide-zinc-900">
              {loadingList && <div className="px-3 py-4 text-sm text-zinc-500">Loading tickets…</div>}
              {!loadingList && filtered.length === 0 && (
                <div className="px-3 py-4 text-sm text-zinc-500">No tickets match.</div>
              )}
              {!loadingList && filtered.map((c) => (
                <button key={c.id} onClick={() => setSurvivor(c)} data-testid={`merge-candidate-${c.id}`}
                  aria-label={`Ticket #${c.number}, ${c.title}, ${clientNames[c.client_id] || "unknown client"}, status ${c.status}`}
                  className="w-full text-left px-3 py-2 hover:bg-zinc-900/60 flex items-center gap-2">
                  <span className="font-mono text-xs text-zinc-500 shrink-0" aria-hidden="true">#{c.number}</span>
                  <span className="text-sm text-zinc-200 truncate flex-1" aria-hidden="true">{c.title}</span>
                  <span className="text-[11px] text-zinc-500 shrink-0" aria-hidden="true">{clientNames[c.client_id] || "—"}</span>
                  <StatusBadge status={c.status} />
                </button>
              ))}
            </div>
          </div>
        )}

        {survivor && (
          <div className="space-y-3">
            <div className="rounded border border-zinc-900 bg-zinc-900/40 p-3 text-sm leading-relaxed text-zinc-300" data-testid="merge-confirm-text">
              Merge <span className="font-mono text-zinc-100">#{ticket.number}</span> — "{ticket.title}"
              {" "}into{" "}
              <span className="font-mono text-zinc-100">#{survivor.number}</span> — "{survivor.title}"?
            </div>
            <ul className="text-xs text-zinc-500 list-disc list-inside space-y-1">
              <li>Every comment, attachment and logged time on #{ticket.number} moves to #{survivor.number}.</li>
              <li>#{ticket.number} closes and becomes read-only, with a link to #{survivor.number}.</li>
              <li>A satisfaction survey still awaiting an answer on #{ticket.number} expires rather than moving.</li>
            </ul>
            {err && (
              <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="merge-error">
                {err}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {survivor ? (
            <>
              <button onClick={() => { setSurvivor(null); setErr(""); }} disabled={busy}
                className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Back</button>
              <button onClick={onClose} disabled={busy}
                className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
              <button onClick={confirmMerge} disabled={busy} data-testid="merge-confirm-btn"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
                {busy ? "Merging…" : `Merge into #${survivor.number}`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
